import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { ShellService } from '../shell/shell.service';

interface SetupStep {
    id: string;
    title: string;
    route: string;
}

/**
 * "Welcome to Inspectmite" setup guide on the dashboards (mockup setup-guide.js). The steps follow
 * the user type, so every link is a page that user can open: administrators get the mockup's five
 * steps; staff the same without the administrator-only Office Fees and Calendar; inspectors their
 * own pages. A step counts as done once it is opened ("Review your profile" starts done, as in the
 * mockup). Done steps and "Hide setup guide" are per-user preferences (ShellService); the account
 * menu's "Setup guide" brings it back.
 */
@Component({
    selector: 'app-setup-guide',
    templateUrl: './setup-guide.component.html',
    styleUrls: ['./setup-guide.component.scss']
})
export class SetupGuideComponent implements OnInit, OnDestroy {
    steps: SetupStep[] = [];
    primary: SetupStep = null;
    done: string[] = [];
    hidden = false;

    private createRoute = '/inspections-order-parties';
    private subscriptions: Subscription[] = [];

    constructor(private shell: ShellService) { }

    ngOnInit() {
        this.subscriptions.push(this.shell.settings$.subscribe(settings => {
            // Create New Inspection opens the first tab of Settings > Inspection Tab Order.
            this.createRoute = settings && settings.inspection_tab_order == 1 ? '/inspections-edit-property' : '/inspections-order-parties';
            this.buildSteps();
        }));
        this.subscriptions.push(this.shell.preferences$.subscribe(() => {
            const done = this.shell.get('setup_guide_done', []);
            this.done = Array.isArray(done) ? done : [];
            this.hidden = this.shell.get('setup_guide_hidden', false) === true;
        }));
        this.buildSteps();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private buildSteps() {
        const level = this.shell.user ? this.shell.user.user_level : '';
        const profile = { id: 'profile', title: 'Review your profile', route: '/my-account' };
        if (level === 'inspector') {
            this.steps = [
                profile,
                { id: 'schedule', title: 'Check your schedule', route: '/calendar' },
                { id: 'inspections', title: 'Open your inspections', route: '/inspections-list' },
                { id: 'clients', title: 'Look up a client', route: '/clients/list' }
            ];
            this.primary = { id: 'schedule', title: 'View My Schedule', route: '/calendar' };
            return;
        }
        const administrator = level === 'administrator';
        this.steps = [
            profile,
            administrator ? { id: 'fees', title: 'Configure inspection fees', route: '/office-fees' } : null,
            { id: 'client', title: 'Create your first client', route: '/clients/add' },
            { id: 'inspection', title: 'Create your first inspection', route: this.createRoute },
            administrator ? { id: 'calendar', title: 'Explore the Calendar', route: '/calendar' }
                : { id: 'inspections', title: 'Review inspection orders', route: '/inspections-list' }
        ].filter(step => !!step);
        this.primary = { id: 'inspection', title: 'Create Your First Inspection', route: this.createRoute };
    }

    isDone(step: SetupStep): boolean {
        return step.id === 'profile' || this.done.indexOf(step.id) !== -1;
    }

    get doneCount(): number {
        return this.steps.filter(step => this.isDone(step)).length;
    }

    complete(id: string) {
        if (this.done.indexOf(id) === -1) {
            this.done = this.done.concat(id);
            this.shell.set('setup_guide_done', this.done);
        }
    }

    hide() {
        this.hidden = true;
        this.shell.set('setup_guide_hidden', true);
        Swal({
            toast: true,
            position: 'bottom-end',
            type: 'info',
            title: 'Setup guide hidden. Reopen it from your account menu.',
            showConfirmButton: false,
            timer: 3500
        });
    }
}
